import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';

type SymbolDraw = 'icon' | 'marker' | 'line' | 'fill';

// Source of truth for USCG symbology
const USCG_CONFIG = {
  absorbentMaterial: { label: 'Absorbent Material', geometry: 'single' },
  boatRamp: { label: 'Boat Ramp', geometry: 'single' },
  proposedBoom: { label: 'Boom', geometry: 'line' },
  completedBoom: { label: 'Completed Boom', geometry: 'line' },
  branch1: { label: 'Branch 1', geometry: 'single' },
  branch2: { label: 'Branch 2', geometry: 'single' },
  branchBoundary: { label: 'Branch Boundary', geometry: 'single' },
  divisionA: { label: 'Division A', geometry: 'single' },
  divisionB: { label: 'Division B', geometry: 'single' },
  divisionBoundary: { label: 'Division Boundary', geometry: 'single' },
  emergencyOperationsCenter: {
    label: 'Emergency Operations Center',
    geometry: 'single',
  },
  fireStation: { label: 'Fire Station', geometry: 'single' },
  firstAidSection: { label: 'First Aid Section', geometry: 'single' },
  hazardOrigin: { label: 'Hazard Origin', geometry: 'single' },
  helibase: { label: 'Helibase', geometry: 'single' },
  helispot: { label: 'Helispot', geometry: 'single' },
  holtCamp: { label: 'Holt Camp', geometry: 'single' },
  hospital: { label: 'Hospital', geometry: 'single' },
  incidentBase: { label: 'Incident Base', geometry: 'single' },
  incidentCommandPost: { label: 'Incident Command Post', geometry: 'single' },
  stagingArea: { label: 'Staging Area', geometry: 'single', draw: 'marker' },
  group: { label: 'Group', geometry: 'single', draw: 'marker' },
  resourceLocation: {
    label: 'Resource Location',
    geometry: 'single',
    draw: 'marker',
  },
  customPoint: { label: 'Custom Point', geometry: 'single', draw: 'marker' },
  customLine: { label: 'Custom Line', geometry: 'line', draw: 'line' },
  jointInformationCenter: {
    label: 'Joint Information Center',
    geometry: 'single',
  },
  mobileRelay: { label: 'Mobile Relay', geometry: 'single' },
  mobileWeatherUnit: { label: 'Mobile Weather Unit', geometry: 'single' },
  oilSpill: { label: 'Oil or Chemical Plume', geometry: 'line' },
  route: { label: 'Route', geometry: 'line', draw: 'line' },
  oilSpillPrediction: { label: 'Oil Spill Prediction', geometry: 'single' },
  customPolygon: { label: 'Custom Polygon', geometry: 'polygon', draw: 'fill' },
  operationalWorkArea: {
    label: 'Operational Work Area',
    geometry: 'polygon',
    draw: 'fill',
  },
  exclusionZone: { label: 'Exclusion Zone', geometry: 'polygon', draw: 'fill' },
  searchArea: { label: 'Search Area', geometry: 'polygon', draw: 'fill' },
  incidentOverlay: {
    label: 'Incident Overlay',
    geometry: 'polygon',
    draw: 'fill',
  },
  policeStation: { label: 'Police Station', geometry: 'single' },
  redfernStagingArea: { label: 'Redfern Staging Area', geometry: 'single' },
  safetySecurityZone: { label: 'Safety/Security Zone', geometry: 'single' },
  telephone: { label: 'Telephone', geometry: 'single' },
  windSpeedAndDirection: {
    label: 'Wind Speed and Direction',
    geometry: 'single',
  },
  chickfilaLocation: { label: 'Chick-fil-A', geometry: 'single' },
  facility: { label: 'Facility', geometry: 'single' },
} as const;

export type UscgSymbol = keyof typeof USCG_CONFIG;
export type SymbolGeometryType = 'single' | 'line' | 'polygon';

type SymbolConfig = {
  label: string;
  geometry: SymbolGeometryType;
  draw?: SymbolDraw;
};

function symbolDraw(symbol: UscgSymbol): SymbolDraw {
  return (USCG_CONFIG[symbol] as SymbolConfig).draw ?? 'icon';
}

/** Symbols that use a PNG asset instead of the default SVG. */
const PNG_SYMBOLS = new Set<UscgSymbol>(['chickfilaLocation']);

export const getSymbolSvgUrl = (name: UscgSymbol) => {
  const ext = PNG_SYMBOLS.has(name) ? 'png' : 'svg';
  return new URL(`../symbols/${name}.${ext}`, import.meta.url).href;
};

export const symbolToGeometryTypeMap = Object.fromEntries(
  Object.entries(USCG_CONFIG).map(([key, val]) => [key, val.geometry])
) as Record<UscgSymbol, SymbolGeometryType>;

const ICON_SYMBOLS = (Object.keys(USCG_CONFIG) as UscgSymbol[]).filter(
  (key) => symbolDraw(key) === 'icon'
);

export const symbolToPictureMarkerMap = Object.fromEntries(
  ICON_SYMBOLS.map((key) => [
    key,
    new PictureMarkerSymbol({
      url: getSymbolSvgUrl(key),
      width: '32px',
      height: '32px',
    }),
  ])
) as Partial<Record<UscgSymbol, PictureMarkerSymbol>>;

export function isFillSymbol(symbol: UscgSymbol): boolean {
  return symbolDraw(symbol) === 'fill';
}

export function isSimpleMarkerSymbol(symbol: UscgSymbol): boolean {
  return symbolDraw(symbol) === 'marker';
}

export function isSimpleLineSymbol(symbol: UscgSymbol): boolean {
  return symbolDraw(symbol) === 'line';
}

/** Custom point and custom polygon take a color and opacity from the form. */
export function usesUserColor(symbol: UscgSymbol): boolean {
  return symbol === 'customPoint' || symbol === 'customPolygon';
}

/** Shown on the add-object form, not on resource or action symbology. */
export function isLayerObjectSymbol(symbol: UscgSymbol): boolean {
  return symbolDraw(symbol) !== 'icon';
}

export const SIMPLE_MARKER_COLOR: Partial<Record<UscgSymbol, string>> = {
  stagingArea: '#f59e0b',
  group: '#0d9488',
  resourceLocation: '#7c3aed',
  customPoint: '#1e40af',
};

const LAYER_SHAPE_SWATCH: Partial<Record<UscgSymbol, string>> = {
  ...SIMPLE_MARKER_COLOR,
  route: '#0f766e',
  customLine: '#1d4ed8',
  customPolygon: '#1e40af',
  operationalWorkArea: '#1e40af',
  exclusionZone: '#b91c1c',
  searchArea: '#0369a1',
  incidentOverlay: '#4b5563',
};

/**
 * Whether a stored string still names a symbol in the catalog.
 *
 * Keys are persisted as plain text with no database constraint, so anything
 * reading one back has to cope with a value from an older or newer catalog.
 */
export const isUscgSymbol = (value: unknown): value is UscgSymbol =>
  typeof value === 'string' && value in USCG_CONFIG;

export const USCG_SYMBOLOGY_OPTIONS = (
  Object.entries(USCG_CONFIG) as [
    UscgSymbol,
    (typeof USCG_CONFIG)[UscgSymbol],
  ][]
).map(([type, config]) => ({
  type,
  label: config.label,
  iconSrc: symbolDraw(type) === 'icon' ? getSymbolSvgUrl(type) : undefined,
  swatch: LAYER_SHAPE_SWATCH[type],
}));
